import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Car, ArrowRight, Users, Minus, Plus } from "lucide-react";
import api from "@/lib/api";
import { inr, prettyDate } from "@/lib/format";
import Shell from "@/components/Shell";
import SearchWidget from "@/components/SearchWidget";

export default function CabResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const pickup = params.get("pickup") || "";
  const drop = params.get("drop") || "";
  const date = params.get("date") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [km, setKm] = useState(Number(params.get("km") || 20));

  useEffect(() => {
    setLoading(true);
    api.get("/cabs/search").then((r) => setResults(r.data.results)).finally(() => setLoading(false));
  }, []);

  const fare = (c) => Math.round(c.base_fare + c.per_km * km);

  const select = (c) => navigate("/checkout", { state: {
    type: "cab", item_id: c.id, title: `${c.type} cab`, image: c.image,
    amount: fare(c), currency: "inr",
    details: { pickup, drop, date, km, type: c.type },
    summary: [{ k: "Pickup", v: pickup || "—" }, { k: "Drop", v: drop || "—" }, { k: "Date", v: date ? prettyDate(date) : "—" }, { k: "Distance", v: `${km} km` }, { k: "Base fare", v: inr(c.base_fare) }],
    icon: "cab",
  }});

  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8"><SearchWidget initialTab="cabs" /></section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl text-foreground mb-1">Cabs {pickup && drop ? `· ${pickup} → ${drop}` : ""}</h1>
            <p className="text-muted-foreground text-sm">{date ? prettyDate(date) : "Pick a cab type below"}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Est. distance</span>
            <div className="flex items-center gap-3 rounded-lg border border-input h-10 px-3">
              <button onClick={() => setKm(Math.max(5, km - 5))} data-testid="km-minus"><Minus size={15} /></button>
              <span className="font-semibold w-14 text-center" data-testid="km-count">{km} km</span>
              <button onClick={() => setKm(km + 5)} data-testid="km-plus"><Plus size={15} /></button>
            </div>
          </div>
        </div>
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">{[...Array(4)].map((_, i) => <div key={i} className="h-32 rounded-2xl bg-secondary/60 animate-pulse" />)}</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2" data-testid="cab-results">
            {results.map((c, i) => (
              <div key={c.id} className="rounded-2xl border border-primary/10 bg-card p-5 flex items-center gap-5 justify-between" data-testid={`cab-${i}`}>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center w-14 h-14 rounded-xl bg-secondary text-accent"><Car size={26} /></span>
                  <div>
                    <p className="font-semibold text-foreground">{c.type}</p>
                    <p className="text-xs text-muted-foreground">{c.desc}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Users size={12} /> up to {c.capacity} · {inr(c.per_km)}/km</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl text-foreground">{inr(fare(c))}</p>
                  <button onClick={() => select(c)} data-testid={`select-cab-${i}`} className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2 text-sm font-semibold hover:-translate-y-px transition-transform">Book <ArrowRight size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
}
